import { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import type { Client } from '@altyor-kronos/shared-types';
import ClientWizard, { type ClientFormData } from '../../admin/ClientWizard';
import { colors } from '../../../core/theme/theme';

export interface StepClientProps {
  clients: Array<{ id: string; data: Client }>;
  selectedClientId: string | null;
  onSelect: (clientId: string) => void;
  onClientCreated: (clientId: string) => void;
  /** Callback to create a client in Firestore; returns the new doc ID */
  onCreateClient: (values: ClientFormData) => Promise<string>;
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export default function StepClient({
  clients,
  selectedClientId,
  onSelect,
  onClientCreated,
  onCreateClient,
}: StepClientProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [clientWizardOpen, setClientWizardOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!search.trim()) return clients;
    const q = search.toLowerCase();
    return clients.filter((c) => c.data.name.toLowerCase().includes(q));
  }, [clients, search]);

  const handleCreateClient = async (values: ClientFormData): Promise<void> => {
    const newId = await onCreateClient(values);
    onClientCreated(newId);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        py: 2,
      }}
    >
      <Typography variant="h3" sx={{ textAlign: 'center' }}>
        {t('projects.wizard.clientQuestion', 'Who is the client?')}
      </Typography>

      {/* Search + New Client */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search')}
          size="small"
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => setClientWizardOpen(true)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {t('admin.clients.new')}
        </Button>
      </Box>

      {/* Client Grid */}
      {filtered.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 4 }}
        >
          {t('common.noResults')}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((c) => {
            const selected = c.id === selectedClientId;
            return (
              <Grid key={c.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  sx={{
                    position: 'relative',
                    border: selected
                      ? `2px solid ${colors.yellow}`
                      : `1px solid ${colors.lightGray}`,
                    backgroundColor: selected ? 'rgba(255, 250, 94, 0.06)' : undefined,
                    '&:hover': {
                      backgroundColor: selected ? 'rgba(255, 250, 94, 0.1)' : colors.peach,
                    },
                  }}
                >
                  <CardActionArea onClick={() => onSelect(c.id)}>
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        minHeight: 120,
                      }}
                    >
                      {/* Checkmark overlay */}
                      {selected && (
                        <CheckCircleIcon
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: colors.red,
                            fontSize: 22,
                          }}
                        />
                      )}

                      {/* Avatar + Name */}
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Avatar
                          src={c.data.logoURL || undefined}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: colors.lightGray,
                            color: colors.bodyText,
                            fontWeight: 700,
                            fontSize: 14,
                          }}
                        >
                          {!c.data.logoURL && getInitials(c.data.name)}
                        </Avatar>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {c.data.name}
                        </Typography>
                      </Box>

                      {/* Industry */}
                      {c.data.industry && (
                        <Chip
                          label={c.data.industry}
                          size="small"
                          sx={{
                            alignSelf: 'flex-start',
                            backgroundColor: colors.lightGray,
                            color: colors.bodyText,
                          }}
                        />
                      )}

                      {/* Location */}
                      {(c.data.country || c.data.city) && (
                        <Typography variant="caption" color="text.secondary">
                          {[c.data.city, c.data.country].filter(Boolean).join(', ')}
                        </Typography>
                      )}

                      {/* Employee range */}
                      {c.data.employeeRange && (
                        <Chip
                          label={c.data.employeeRange}
                          size="small"
                          variant="outlined"
                          sx={{ alignSelf: 'flex-start' }}
                        />
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Client Wizard Dialog (stacked on top) */}
      <ClientWizard
        open={clientWizardOpen}
        allClients={clients.map((c) => ({ id: c.id }))}
        onSubmit={handleCreateClient}
        onClose={() => setClientWizardOpen(false)}
      />
    </Box>
  );
}
